import Recaptcha from '@components/App/RecaptchaWidget'
import { Input } from '@lib/components'
import { CollapsableBlock } from '@lib/components/CollapsableBlock'
import { IconWorld } from '@lib/icons'
import { AiBlocks, WithMetaTags } from '@lib/images'
import { Button } from '@octopus-do/octopus-storybook'
import cx from 'classnames'
import { type MutableRefObject, useState } from 'react'
import { useMount } from 'react-use'

import { GtmTriggerName } from '@/constants/gtm'
import { useFeatureFlags } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import useSet from '@/hooks/useSet'
import type { PageNameSource } from '@/types'
import { useStartCrawl, useUpgradePlan } from '../../../hooks'
import globalCss from '../../../Import.module.scss'
import { BlocksTextOption } from '../../BlocksTextOption'
import { ExcludePathBlock } from '../../ExcludePathBlock'
import { LimitNotice } from '../../LimitNotice'
import { PageNamesOption } from '../../PageNamesOption'
import { Selectors } from '../../Selectors'
import { SELECTORS } from '../constants'
import { prepareUrl } from '../utils'
import css from './CrawlerTab.module.scss'

// What each card actually imports.
const MODE_DESCRIPTION = {
  standard:
    'Imports the sitemap structure and SEO data. Quick and lightweight.',
  blocks:
    'Imports the sitemap together with page content, laid out as blocks. Text content is optional — see Settings.',
}

type Props = {
  teamId?: string
  folderId?: string | null
  url: string
  setUrl: (url: string) => void
  activeSelector: string
  setActiveSelector: (activeSelector: string) => void
  immediatelyRef: MutableRefObject<boolean>
  // Land the crawl in the project the user already has open.
  intoOpenProject?: boolean
  initialBlocksContent?: boolean
  initialExcludedUrls?: string[]
  initialPageNames?: PageNameSource
}

const CrawlerTab: React.FC<Props> = ({
  teamId,
  folderId,
  url,
  setUrl,
  activeSelector,
  setActiveSelector,
  immediatelyRef,
  intoOpenProject = false,
  initialBlocksContent = false,
  initialExcludedUrls = [],
  initialPageNames = 'url',
}) => {
  const {
    crawler: { userHasMaximumLimit },
  } = useFeatureFlags()

  const [excludedPaths, { add: addExcludedPath, delete: deleteExcludedPath }] =
    useSet<string>(initialExcludedUrls)
  const [blocksText, setBlocksText] = useState(initialBlocksContent)
  const [pageNames, setPageNames] = useState(initialPageNames)
  // The selector is shared with the sitemap tab, whose own cards both mean
  // the standard crawl here: pages with their meta tags.
  const withBlocks = activeSelector === SELECTORS.WITH_BLOCKS
  const activeCard = withBlocks ? SELECTORS.WITH_BLOCKS : SELECTORS.STANDARD

  const { loading, inputError, startCrawl } = useStartCrawl({
    teamId,
    folderId,
    intoOpenProject,
    // Both modes carry the meta tags; only the blocks tell them apart.
    metaTag: true,
    blocks: withBlocks,
    blocksContent: withBlocks && userHasMaximumLimit && blocksText,
    excludedUrls: Array.from(excludedPaths),
    pageNames,
    asReport: false,
  })

  // The open project takes no pages while the artboard is off, so the crawl
  // would end in an error (see useCommitIntoProject).
  const artboardDisabled = useArtBoardDisabled()
  const importDisabled = intoOpenProject && artboardDisabled

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()

    if (importDisabled) {
      return
    }

    startCrawl(url)
  }

  useMount(() => {
    if (immediatelyRef.current) {
      handleSubmit()
      immediatelyRef.current = false
    }
  })

  const onBlur = () => {
    setUrl(prepareUrl(url))
  }

  const upgrade = useUpgradePlan()

  const openUpgrade = () =>
    upgrade(
      GtmTriggerName.UPGRADE_AI_BLOCKS,
      'Click on the PRO badge of the block text option on a free plan'
    )

  // Blocks cost model calls, so a free AI crawl stops earlier than a standard one.
  const freeLimit = withBlocks ? 150 : 300

  return (
    <Recaptcha>
      <form onSubmit={handleSubmit}>
        <p className={globalCss.description}>
          The crawler allows you to import an exact sitemap, but the import can
          take a several minutes.
        </p>

        <div className={css.input}>
          <Input
            autoFocus
            initialValue={url}
            placeholder="https://website.com"
            name="url"
            type="normal"
            icon={<IconWorld />}
            onChange={setUrl}
            error={inputError ? 'Please enter a valid URL' : undefined}
            autoComplete="off"
            spellCheck="false"
            onBlur={onBlur}
            onSubmit={() => handleSubmit()}
          />
        </div>

        <Selectors
          items={[
            {
              id: SELECTORS.STANDARD,
              label: 'Standard',
              icon: <WithMetaTags />,
              badge: null,
            },
            {
              id: SELECTORS.WITH_BLOCKS,
              label: 'Content',
              icon: <AiBlocks />,
              // The card itself is open to every plan; only the block text is PRO.
              badge: 'beta',
            },
          ]}
          onChange={setActiveSelector}
          activeItemId={activeCard}
        />

        {/* Both lines share one grid cell, so the block is as tall as the longer
            of them and Settings/Crawl stay put when the card changes. */}
        <div className={css.modeDescription}>
          <p
            className={cx({ [css.hidden]: withBlocks })}
            aria-hidden={withBlocks}
          >
            {MODE_DESCRIPTION.standard}
          </p>
          <p
            className={cx({ [css.hidden]: !withBlocks })}
            aria-hidden={!withBlocks}
          >
            {MODE_DESCRIPTION.blocks}
          </p>
        </div>

        {!userHasMaximumLimit && (
          <LimitNotice>Free plan limit: {freeLimit} pages</LimitNotice>
        )}

        <CollapsableBlock
          className={css.collapsableBlock}
          title="Settings"
          animated
        >
          {withBlocks && (
            <BlocksTextOption
              className={css.option}
              checked={blocksText}
              pro={userHasMaximumLimit}
              onChange={setBlocksText}
              onUpgrade={openUpgrade}
            />
          )}

          <PageNamesOption value={pageNames} onChange={setPageNames} />

          <ExcludePathBlock
            excludedPaths={excludedPaths}
            addExcludedPath={addExcludedPath}
            deleteExcludedPath={deleteExcludedPath}
            baseUrl={url}
          />
        </CollapsableBlock>

        <div className={globalCss.button}>
          <Button
            label="Crawl"
            loading={loading}
            disabled={!url || importDisabled}
            data-testid="crawl"
            onClick={handleSubmit}
          />
        </div>
      </form>
    </Recaptcha>
  )
}

export default CrawlerTab
